import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@pulpo/app-config';
import {
  ExplainInput,
  ExplainOutput,
  LuggageCatalogInput,
  LuggageCatalogOutput,
  OrderInput,
  OrderOutput,
  SearchInput,
  SearchOutput,
  UpsellInput,
  UpsellOutput,
  ValidateInput,
  ValidateOutput,
} from '@pulpo/pulpo-models';
import { Observable } from 'rxjs';
import ApiService from '../api.service.abstract';

@Injectable({ providedIn: 'root' })
export class FlightSearchService extends ApiService<never> {
  constructor(
    @Inject(APP_CONFIG) environment: any,
    protected http: HttpClient
  ) {
    super(environment, http, 'flight/api/flight-search');
  }

  // Exposed so the debug panel can show which endpoint a given exchange
  // actually hit, next to its raw request/response.
  get searchUrl(): string {
    return `${this.url}/${this.endpoint}/search`;
  }
  get quoteUrl(): string {
    return `${this.url}/${this.endpoint}/quote`;
  }
  get bookUrl(): string {
    return `${this.url}/${this.endpoint}/book`;
  }
  get upsellUrl(): string {
    return `${this.url}/${this.endpoint}/upsell`;
  }
  get luggageCatalogUrl(): string {
    return `${this.url}/${this.endpoint}/luggage-catalog`;
  }
  get explainUrl(): string {
    return `${this.url}/${this.endpoint}/explain`;
  }

  search(input: SearchInput): Observable<HttpResponse<SearchOutput>> {
    return this.http.post<SearchOutput>(this.searchUrl, input, {
      observe: 'response',
    });
  }

  quote(input: ValidateInput): Observable<HttpResponse<ValidateOutput>> {
    return this.http.post<ValidateOutput>(this.quoteUrl, input, {
      observe: 'response',
    });
  }

  book(input: OrderInput): Observable<HttpResponse<OrderOutput>> {
    return this.http.post<OrderOutput>(this.bookUrl, input, {
      observe: 'response',
    });
  }

  upsell(input: UpsellInput): Observable<HttpResponse<UpsellOutput>> {
    return this.http.post<UpsellOutput>(this.upsellUrl, input, {
      observe: 'response',
    });
  }

  // Amadeus-only on-demand catalog (PUL-3030) - fetched per offer via its
  // transactionId, on demand, since /search no longer builds it upfront for
  // this provider. Travelport/Duffel keep getting it inline from /search.
  luggageCatalog(input: LuggageCatalogInput): Observable<HttpResponse<LuggageCatalogOutput>> {
    return this.http.post<LuggageCatalogOutput>(this.luggageCatalogUrl, input, {
      observe: 'response',
    });
  }

  // PUL-3016: technical-admin-only (stricter than the other endpoints on
  // this screen) - AI summary of the transaction from whatever's already on
  // screen (debug exchanges, offer). Can take up to ~30s, no client-side
  // timeout added here - a slow-but-eventually-successful call must not be
  // aborted.
  explain(input: ExplainInput): Observable<HttpResponse<ExplainOutput>> {
    return this.http.post<ExplainOutput>(this.explainUrl, input, {
      observe: 'response',
    });
  }
}
